import { useState } from "react";
import { message } from "antd";

import { Field, Label, Description } from "V2.0/common/fieldset";
import { Button } from "V2.0/common/button";
import { MultiListbox } from "V2.0/components/MultiListBox";

import { putTranscriptionSettings } from "../service";
import { generateTranscriptionSettingsPayload } from "./models/transcriptionSettings";
import { SonioxModel } from "V2.0/HomePages/Assistants/TranscriptionSettingsWithProviders/models/soniox";

// Soniox ("State of the Art") language options are namespaced so their codes
// (e.g. "en") don't collide with the legacy provider codes in the same picker.
export const SONIOX_TRANSCRIPTION_PREFIX = "soniox::";

const MAX_STATE_OF_THE_ART_LANGUAGES = 3;

function isStateOfTheArtValue(value) {
  return (
    typeof value === "string" && value.startsWith(SONIOX_TRANSCRIPTION_PREFIX)
  );
}

function getSection(value) {
  return isStateOfTheArtValue(value) ? "stateOfTheArt" : "legacy";
}

function TranscriptionSettings({
  language,
  languageOptions,
  languages,
  sonioxModel,
  workspaceId,
  assistantId,
  syncAssistant,
  onSuccess,
}) {
  const [selectedLanguages, setSelectedLanguages] = useState(language || []);
  const [loading, setLoading] = useState(false);

  const handleChange = (nextValues) => {
    const addedValues = nextValues.filter(
      (value) => !selectedLanguages.includes(value)
    );

    if (addedValues.length > 0) {
      const addedValue = addedValues[addedValues.length - 1];

      // Legacy is single-select; State of the Art allows multiple. Selecting
      // across sections is never allowed.
      if (getSection(addedValue) === "legacy") {
        setSelectedLanguages([addedValue]);
        return;
      }

      const stateOfTheArtValues = nextValues.filter(
        (value) => getSection(value) === "stateOfTheArt"
      );

      if (stateOfTheArtValues.length > MAX_STATE_OF_THE_ART_LANGUAGES) {
        message.warning(
          `You can select up to ${MAX_STATE_OF_THE_ART_LANGUAGES} State of the Art languages`
        );
        return;
      }

      setSelectedLanguages(stateOfTheArtValues);
      return;
    }

    setSelectedLanguages(nextValues);
  };

  const buildPayload = () => {
    if (selectedLanguages.length === 0) return null;

    if (isStateOfTheArtValue(selectedLanguages[0])) {
      // Soniox expects base language codes, so drop any region suffix
      // ("en-US" -> "en") and dedupe.
      const languageHints = Array.from(
        new Set(
          selectedLanguages.map((value) => {
            const languageId = value.slice(SONIOX_TRANSCRIPTION_PREFIX.length);
            return languageId.split("-")[0];
          })
        )
      );

      return {
        transcriptionProvider: "custom",
        sonioxSettings: {
          model: sonioxModel || SonioxModel.STT_RT_V4,
          languageHints,
        },
      };
    }

    // Legacy is single-select and keeps its original per-language provider.
    const languageId = selectedLanguages[0];
    const transcriptionProvider =
      languages[languageId]?.transcriptionProvider || "deepgram";

    return generateTranscriptionSettingsPayload(
      languageId,
      transcriptionProvider
    );
  };

  const handleSave = async () => {
    const payload = buildPayload();

    if (!payload) {
      message.error("Please select at least one language");
      return;
    }

    setLoading(true);

    try {
      const response = await putTranscriptionSettings({
        workspaceId,
        assistantId,
        payload,
      });
      if (response.status === 200) {
        message.success("Language updated");
        setLoading(false);
        syncAssistant((prevAssistant) => {
          return {
            ...prevAssistant,
            transcriptionSettings: response.data,
          };
        });
        onSuccess();
      }
    } catch (e) {
      message.error(
        e.response?.data?.msg ||
          e.response?.data?.message ||
          e.response?.data?.error ||
          "Failed to update language"
      );
      setLoading(false);
      console.error("Error updating transcription settings");
    }
  };

  const getSectionLabel = (value) =>
    getSection(value) === "stateOfTheArt" ? "State of the Art" : "Legacy";

  const getOptionLabel = (value) => {
    const languageId = isStateOfTheArtValue(value)
      ? value.slice(SONIOX_TRANSCRIPTION_PREFIX.length)
      : value;
    return languages[languageId]?.label || value;
  };

  // Show the section name in brackets next to each selected language.
  const getDisplayValue = (value) =>
    `${getOptionLabel(value)} (${getSectionLabel(value)})`;

  return (
    <div className="flex flex-col gap-y-4">
      <Field>
        <Label>Language</Label>
        <Description>
          Select up to {MAX_STATE_OF_THE_ART_LANGUAGES} languages under State of
          the Art, or a single language under Legacy.
        </Description>
        <MultiListbox
          className="my-4"
          options={languageOptions}
          value={selectedLanguages}
          onChange={handleChange}
          placeholder="Select language(s)"
          displayValue={getDisplayValue}
          matchButtonWidth
          singleValueAsText={
            selectedLanguages.length === 1 &&
            getSection(selectedLanguages[0]) === "legacy"
          }
        />
      </Field>
      <div className="flex justify-end">
        <Button
          loading={loading}
          onClick={handleSave}
        >
          Save
        </Button>
      </div>
    </div>
  );
}

export default TranscriptionSettings;
